"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "next/navigation";
import { Sparkles, Menu, ArrowDown } from "lucide-react";
import Link from "next/link";
import { useUser } from "@/lib/user-context";
import { useChatStream, type ChatRef, type CostInfo } from "@/lib/hooks/use-chat-stream";
import { ChatMessage } from "@/components/chat/chat-message";
import { ChatInput, type ModelKey, type SpeedKey } from "@/components/chat/chat-input";
import { ThinkingIndicator } from "@/components/chat/thinking-indicator";
import { ChatSidebar } from "@/components/chat/chat-sidebar";
import { SuggestedQuestions } from "@/components/chat/suggested-questions";
import { FollowUpQuestions } from "@/components/chat/follow-up-questions";
import { LoginPromptModal, useLoginPrompt } from "@/components/common/login-prompt-modal";
import { ChatPageSkeleton } from "@/components/common/skeleton";

interface Message {
  id?: number;
  role: "user" | "assistant";
  content: string;
  refs?: ChatRef[];
  cost?: CostInfo | null;
  ragContext?: string | null;
  parsedQuery?: Record<string, string[]> | null;
}

const API_BASE = "/api/v1";

export default function ChatPage() {
  const { me, loading: authLoading } = useUser();
  const stream = useChatStream();
  const searchParams = useSearchParams();
  const initialQuerySent = useRef(false);

  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = me?.user?.user_type === "admin";
  const [model, setModel] = useState<ModelKey>("flash-lite");
  const [speed, setSpeed] = useState<SpeedKey>(0);
  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);
  const { modalState, requireLogin, closeModal } = useLoginPrompt();

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevStreamingRef = useRef(false);
  const [showScrollDown, setShowScrollDown] = useState(false);

  // 자동 스크롤 (사용자 클릭 = smooth, 자동 = instant)
  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "instant" });
    }
  }, []);

  // 스크롤 위치 감지 — 하단에서 100px 이상 떨어지면 화살표 표시
  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollDown(distFromBottom > 100);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll, messages, stream.content]);

  // 스트리밍 중: 사용자 메시지가 최상단에 걸칠 때까지 자동 스크롤
  const targetScrollRef = useRef<number | null>(null);

  useEffect(() => {
    if (!stream.isStreaming) {
      targetScrollRef.current = null;
      return;
    }

    const el = scrollRef.current;
    if (!el) return;

    // 타겟 스크롤 위치 계산 (한 번만)
    if (targetScrollRef.current === null) {
      const userMsgs = el.querySelectorAll("[data-role='user']");
      const lastUserMsg = userMsgs[userMsgs.length - 1] as HTMLElement | undefined;
      if (lastUserMsg) {
        // 사용자 메시지 상단이 컨테이너 상단에 오는 scrollTop 값
        targetScrollRef.current = lastUserMsg.offsetTop - 8;
      }
    }

    const target = targetScrollRef.current;
    if (target === null || target <= 0) return;

    // 현재 스크롤이 타겟보다 작으면 타겟까지 스크롤
    if (el.scrollTop < target) {
      // 콘텐츠가 충분히 길어졌을 때만 (스크롤 가능한 상태)
      const maxScroll = el.scrollHeight - el.clientHeight;
      el.scrollTop = Math.min(target, maxScroll);
    }
  }, [stream.content, stream.isStreaming]);

  // 스트리밍 완료 시 메시지 목록에 추가 (동기 setState 회피 — raf로 defer)
  useEffect(() => {
    if (prevStreamingRef.current && !stream.isStreaming && stream.content) {
      const snapshot = {
        id: stream.messageId ?? undefined,
        role: "assistant" as const,
        content: stream.content,
        refs: stream.refs,
        cost: stream.cost,
        ragContext: stream.ragContext,
        parsedQuery: stream.parsedQuery,
      };
      const convId = stream.conversationId;
      const raf = requestAnimationFrame(() => {
        setMessages((prev) => [...prev, snapshot]);
        if (convId) setConversationId(convId);
        setSidebarRefreshKey((k) => k + 1);
      });
      prevStreamingRef.current = stream.isStreaming;
      return () => cancelAnimationFrame(raf);
    }
    prevStreamingRef.current = stream.isStreaming;
  }, [
    stream.isStreaming,
    stream.content,
    stream.conversationId,
    stream.messageId,
    stream.refs,
    stream.cost,
    stream.ragContext,
    stream.parsedQuery,
  ]);

  // 실제 메시지 전송 (로그인 상태에서만 호출)
  const doSend = useCallback(
    (message: string) => {
      setMessages((prev) => [...prev, { role: "user", content: message }]);
      stream.send(message, conversationId, model, isAdmin ? speed : undefined);
      setTimeout(() => scrollToBottom(false), 50);
    },
    [stream, conversationId, model, scrollToBottom, isAdmin, speed],
  );

  // 전송 버튼 핸들러 — 비로그인 시 로그인 모달
  const pendingMessageRef = useRef<string | null>(null);
  const handleSend = useCallback(
    (message: string) => {
      if (me?.user) {
        doSend(message);
      } else {
        pendingMessageRef.current = message;
        requireLogin(false, "chat", () => {});
      }
    },
    [me, doSend, requireLogin],
  );

  // URL ?q= 파라미터로 자동 질문 전송 (동기 setState 회피 — 다음 tick에 전송)
  useEffect(() => {
    const q = searchParams.get("q");
    if (q && me && !initialQuerySent.current && !stream.isStreaming) {
      initialQuerySent.current = true;
      const t = setTimeout(() => handleSend(q), 0);
      return () => clearTimeout(t);
    }
  }, [me, searchParams, stream.isStreaming, handleSend]);

  // 대화 선택
  const handleSelectConversation = useCallback(async (id: number) => {
    try {
      const res = await fetch(`${API_BASE}/chat/conversations/${id}`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const data = await res.json();
      setConversationId(id);
      setMessages(
        (data.messages || []).map(
          (m: {
            id: number;
            role: string;
            content: string;
            context_refs?: string[];
            cost?: CostInfo | null;
          }) => ({
            id: m.id,
            role: m.role as "user" | "assistant",
            content: m.content,
            refs: m.context_refs,
            cost: m.cost || null,
          }),
        ),
      );
      setSidebarOpen(false);
      setTimeout(() => {
        const el = document.getElementById("chat-scroll");
        if (el) el.scrollTop = el.scrollHeight;
      }, 50);
    } catch {
      // 무시
    }
  }, []);

  // 새 대화
  const handleNewConversation = useCallback(() => {
    setConversationId(null);
    setMessages([]);
    setSidebarOpen(false);
  }, []);

  // 로딩 중 — skeleton UI
  if (authLoading) {
    return <ChatPageSkeleton />;
  }

  const isLoggedIn = !!me?.user;

  return (
    <div className="flex h-[calc(100dvh-3.5rem)]">
      {/* 사이드바 — 데스크톱 (로그인 시만) */}
      {isLoggedIn && (
        <div className="hidden w-72 shrink-0 lg:block">
          <ChatSidebar
            currentId={conversationId}
            onSelect={handleSelectConversation}
            onNew={handleNewConversation}
            refreshKey={sidebarRefreshKey}
          />
        </div>
      )}

      {/* 사이드바 — 모바일 오버레이 (로그인 시만) */}
      {isLoggedIn && sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <div className="absolute top-0 left-0 h-full w-72 shadow-xl">
            <ChatSidebar
              currentId={conversationId}
              onSelect={handleSelectConversation}
              onNew={handleNewConversation}
              refreshKey={sidebarRefreshKey}
              onClose={() => setSidebarOpen(false)}
            />
          </div>
        </div>
      )}

      {/* 메인 채팅 영역 */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* 상단 바 */}
        <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5">
          {isLoggedIn && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 lg:hidden"
              aria-label="대화 목록"
            >
              <Menu className="h-4.5 w-4.5" />
            </button>
          )}
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-violet-700">
              <Sparkles className="h-3 w-3 text-white" strokeWidth={2} />
            </div>
            <span className="text-sm font-semibold text-gray-800">skinagent AI</span>
          </div>
        </div>

        {/* 메시지 영역 */}
        <div id="chat-scroll" ref={scrollRef} className="flex-1 overflow-y-auto">
          {messages.length === 0 && !stream.isStreaming ? (
            /* 빈 상태 */
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-50">
                <Sparkles className="h-7 w-7 text-violet-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">무엇이 궁금하세요?</h3>
              <p className="max-w-xs text-center text-sm text-gray-400">
                4.5만편의 PubMed 논문을 분석한 AI가 근거 기반으로 답변합니다.
              </p>
              <SuggestedQuestions onSelect={(q) => handleSend(q)} />
            </div>
          ) : (
            <div className="py-4">
              {messages.map((msg, i) => (
                <ChatMessage
                  key={msg.id || `msg-${i}`}
                  role={msg.role}
                  content={msg.content}
                  refs={msg.refs}
                  cost={msg.cost}
                  ragContext={msg.ragContext}
                  parsedQuery={msg.parsedQuery}
                />
              ))}
              {/* 대기 중 (검색/분석) */}
              {stream.isStreaming && stream.status && !stream.content && (
                <ThinkingIndicator status={stream.status} key={stream.status.step} />
              )}
              {/* 스트리밍 중 또는 messages에 아직 반영 안 된 완료 메시지 */}
              {stream.content &&
                (stream.isStreaming || !messages.some((m) => m.id === stream.messageId)) && (
                  <ChatMessage
                    role="assistant"
                    content={stream.content}
                    refs={stream.refs}
                    isStreaming={stream.isStreaming}
                  />
                )}
              {/* 에러 표시 */}
              {stream.error && (
                <div className="px-4 py-2">
                  <div
                    role="alert"
                    className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600"
                  >
                    {stream.error}
                    {stream.error.includes("한도") && (
                      <Link
                        href="/chat/premium"
                        className="mt-2 flex items-center justify-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        프리미엄으로 무제한 이용하기
                      </Link>
                    )}
                  </div>
                </div>
              )}
              {/* 후속 질문 추천 */}
              {!stream.isStreaming && stream.followUps.length > 0 && (
                <FollowUpQuestions questions={stream.followUps} onSelect={(q) => handleSend(q)} />
              )}
            </div>
          )}
        </div>

        {/* 맨 아래로 스크롤 버튼 */}
        {showScrollDown && (
          <div className="relative">
            <button
              onClick={() => scrollToBottom()}
              className="animate-in fade-in slide-in-from-bottom-2 absolute -top-12 left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-lg shadow-gray-200/50 transition-all duration-200 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-600"
              aria-label="맨 아래로"
            >
              <ArrowDown className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* 입력 영역 */}
        <ChatInput
          onSend={handleSend}
          onStop={stream.stop}
          isStreaming={stream.isStreaming}
          model={model}
          onModelChange={setModel}
          showModelSelect={isAdmin}
          speed={speed}
          onSpeedChange={isAdmin ? setSpeed : undefined}
        />
      </div>
      <LoginPromptModal open={modalState.open} onClose={closeModal} trigger={modalState.trigger} />
    </div>
  );
}
